import React, { useState, useEffect } from 'react';
import TableHeader from '../components/data-tables/TableHeader';
import TableToolbar from '../components/data-tables/TableToolbar';
import DataTable from '../components/data-tables/DataTable';
import AddWellTestModal from '../features/add-well-test/addWellTestModal';
import AddTaskModal from '../components/add-task-modal/AddTaskModal';
import AddWellNoteModal from '../components/add-wellnote-modal/AddWellNoteModal';
import { exportToExcel } from '../utilities/ExportToExcel';
import { formatDate } from '../utilities/dateHelpers';
import { useAppDispatch, useAppSelector } from '../hooks/storeHooks';
import { DashboardConfig, saveUserPerferences } from '../dispatchers/UserPreferenceDispatcher';
import { updateWellTestTable } from '../components/slices/ColumnSelectorSlice';
import AddGLWellTestModal from '../features/add-glwell-test/addGLWelltestModal';
import { fetchInstallationsForDropdown } from '../dispatchers/WellTestDispatcher';
import AddDefaultWellTestModal from '../features/add-defaultwell-test/addDefaultWellTestModal';
import useConfig from '../hooks/useConfig';
import { fetchDefaultWellTestDataAsync } from '../dispatchers/DefaultWellTestDispatcher';
import { R, useRBAC } from '../rbac';

interface WellTestTableSectionProps {
  wellTestIdData: any;
  isAddTestModalOpen: boolean;
  setIsAddTestModalOpen: (value: boolean) => void;
  isAddTaskModalOpen: boolean;
  setIsAddTaskModalOpen: (value: boolean) => void;
  isAddNoteWell: boolean;
  setIsAddNoteWell: (value: boolean) => void;
  sortedColumnObjects: any[];
  dataTableRef: React.RefObject<HTMLDivElement>;
  columnObjects: any[];
  wellTestWidget: any;
  assetId: string | undefined;
  testWellData: any[];
  handleTaskSaved: () => void;
  setSaveConfigResponse: (value: boolean) => void;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
  widgetName: string;
  wellId?: number | undefined;
}

export default function WellTestTableWidget({
  wellTestIdData,
  isAddTestModalOpen,
  setIsAddTestModalOpen,
  isAddTaskModalOpen,
  setIsAddTaskModalOpen,
  isAddNoteWell,
  setIsAddNoteWell,
  sortedColumnObjects,
  dataTableRef,
  columnObjects,
  wellTestWidget,
  assetId,
  testWellData,
  handleTaskSaved,
  setSaveConfigResponse,
  setSuccessMessage,
  setErrorMessage,
  widgetName,
  wellId
}: WellTestTableSectionProps) {
  const dispatch = useAppDispatch();
  const dashboardName = useAppSelector((state) => state.AssetAnalysis.chartType);
  const industryApplicationId = useAppSelector((state) => state.AssetAnalysis.industryApplicationId);
  const selectedAsset = useAppSelector((state) => state.assetGroups.selectedAsset);
  const [isESP, setESP] = useState([7, 4].includes(Number(industryApplicationId)));

  const isGLDashboard = dashboardName?.toLowerCase().includes('gl');
  const isRLDashboard = dashboardName?.toLowerCase().includes('rl');
  const { enablePumpChecker } = useConfig();
  const effectivePumpChecker = enablePumpChecker && !isRLDashboard;
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [isConfigCheckBoxClicked, setsConfigCheckBoxClicked] = useState(false);
  const [applyTrigger, setApplyTrigger] = useState(false);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstallNumber, setSelectedInstallNumber] = useState<number | null>(null);
  const [columnObjectsState, setColumnObjectsState] = useState(columnObjects);
  const [filteredRows, setFilteredRows] = useState<any[]>([]);
  const [allColumnsForToolbar, setAllColumnsForToolbar] = useState<string[]>([]);

  const disableAddTest = Boolean(selectedInstallNumber === null && effectivePumpChecker);

  const resource = R.widget(dashboardName, widgetName);
  const { can } = useRBAC();
  const rPermission = {
    canAddTest: can(resource, 'addTest'),
    canEditTest: can(resource, 'editTest'),
    canDeleteTest: can(resource, 'deleteTest'),
    canExport: can(resource, 'export'),
    canConfigureColumns: can(resource, 'configureColumns'),
  };

  useEffect(() => {
    industryApplicationId && setESP([7, 4].includes(industryApplicationId));
  }, [industryApplicationId]);


  // Initialize visible columns from sortedColumnObjects
  useEffect(() => {
    if (sortedColumnObjects && sortedColumnObjects.length > 0) {
      const columns = sortedColumnObjects
        .map((col: any) => col.key)
        .filter((key: string) => effectivePumpChecker || key !== 'Info');
      setAllColumnsForToolbar(columns);
      const visibleKeys = sortedColumnObjects
        .filter((obj: any) => obj.selected)
        .map((obj: any) => obj.key)
        .filter((key: string) => effectivePumpChecker || key !== 'Info');
      setVisibleColumns(visibleKeys);
      setApplyTrigger(true);
      setTimeout(() => {
        setApplyTrigger(false);
      }, 1000);
    }
  }, [wellTestWidget, assetId, columnObjects, sortedColumnObjects, enablePumpChecker]);

  const fixedColumns = effectivePumpChecker ? ['Icon', 'Date', 'Info'] : ['Icon', 'Date'];

  // Update column objects state when columnObjects change
  useEffect(() => {
    if (JSON.stringify(columnObjects) !== JSON.stringify(columnObjectsState)) {
      setColumnObjectsState(columnObjects);
    }
  }, [columnObjects, columnObjectsState]);

  // Create dynamic header map from column objects
  const dynamicHeaderMap = columnObjects.reduce(
    (acc: Record<string, string>, col: any) => {
      acc[col.key] = col.label;
      return acc;
    },
    {} as Record<string, string>,
  );

  const fullHeaderMap: Record<string, string> = {
    Icon: '',
    Date: 'Date',
    ...(effectivePumpChecker && { Info: 'Info' }),
    ...dynamicHeaderMap,
  };

  function getColumnHeader(columnId: string): string {
    return (fullHeaderMap && fullHeaderMap[columnId]) || columnId;
  }

  function updateColumns(preferences: any, columns: any) {
    preferences.forEach((pref: any) => {
      const columnIndex = columns.findIndex((col: any) => col.key === pref.Key);
      if (columnIndex !== -1) {
        columns[columnIndex].selected = pref.Selected;
        columns[columnIndex].order = pref.Order;
      }
    });
    columns.sort((a: any, b: any) => a.order - b.order);
  }

  const handleExportXLS = () => {
    const additionalColumns = ['Well name', 'UWI', 'MOP'];
    const removeColumns = ['Icon', 'Info'];
    const exportFixedColumns = effectivePumpChecker ? fixedColumns : ['Icon', 'TestDate'];

    const filteredVisibleColumns = visibleColumns.filter((col) => !exportFixedColumns.includes(col));

    let exportColumns = [...additionalColumns, ...exportFixedColumns, ...filteredVisibleColumns];

    exportColumns = exportColumns.filter((col) => !removeColumns.includes(col));

    if (!exportColumns.includes('Comment') && effectivePumpChecker) {
      exportColumns.push('Comment');
    }

    const columnHeaders: Record<string, string> = {};
    exportColumns.forEach((col) => {
      columnHeaders[col] = getColumnHeader(col);
    });

    const mop = isGLDashboard ? 'GL' : isRLDashboard ? 'RL' : 'ESP';
    const fileName = `${wellTestIdData?.Name || selectedAsset?.assetName || assetId}-${mop}-Well-Tests-${new Date()
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, '')}.xlsx`;

    const updatedTestWellData = (filteredRows.length > 0 ? filteredRows : testWellData).map((testWell: any) => {
      const row: Record<string, any> = {};
      exportColumns.forEach((col) => {
        if (col === 'Well name') {
          row[col] = wellTestIdData?.Name || selectedAsset?.assetName || '';
        } else if (col === 'UWI') {
          row[col] = wellTestIdData?.Identifier || assetId || '';
        } else if (col === 'MOP') {
          row[col] = isGLDashboard ? 'GL' : isRLDashboard ? 'RL' : wellTestIdData?.MethodOfProduction || '';
        } else if (col === 'Comment' && effectivePumpChecker) {
          row[col] = testWell?.Comment ?? '';
        } else if (col === 'Date' || col === 'TestDate') {
          row[col] = formatDate(testWell?.[col], false);
        } else {
          const value = testWell?.[col] ?? '';
          if (typeof value === 'number' && col !== 'InstallNumber' && col !== 'Id') {
            row[col] = value.toFixed(2);
          } else {
            row[col] = value;
          }
        }
      });
      return row;
    });

    exportToExcel(updatedTestWellData, exportColumns, columnHeaders, fileName);
  };

  const handleUpdateColumns = (updated: { id: string; Header: string; isVisible: boolean }[]) => {
    const newVisible = updated.filter((col) => col.isVisible).map((col) => col.id);
    setVisibleColumns(newVisible);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns((prev) => {
      let updatedColumns = [...prev];
      if (updatedColumns.includes(columnId)) {
        if (updatedColumns.filter((id) => !fixedColumns.includes(id)).length === 1) return updatedColumns;
        updatedColumns = updatedColumns.filter((id) => id !== columnId);
      } else {
        updatedColumns.push(columnId);
      }

      const updatedColumnsWithSelection = columnObjectsState.map((column: any) => {
        if (column.key === columnId) {
          return { ...column, selected: !column.selected };
        }
        return column;
      });

      setColumnObjectsState(updatedColumnsWithSelection);
      setVisibleColumns(updatedColumns);

      return updatedColumns;
    });
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleSaveColumns = () => {
    console.log('Columns saved:', visibleColumns);
  };

  const handleSaveConfig = (config: boolean) => {
    setsConfigCheckBoxClicked(config);
  };

  const handleReset = () => {
    const sortedColumns = [...columnObjects].sort((a, b) => a.order - b.order);

    const visibleKeys: string[] = sortedColumns
      .map((col) => col.key)
      .filter((key: string) => effectivePumpChecker || key !== 'Info');

    if (JSON.stringify(visibleKeys) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(visibleKeys);
      setIsResetClicked(true);
      setTimeout(() => {
        setIsResetClicked(false);
      }, 1000);
    }
    setAllColumnsForToolbar(visibleColumns);
  };

  const handleInstallationSelect = (installNumber: number | null) => {
    setSelectedInstallNumber(installNumber);
  };

  const handleApplyClicked = async (source: string, isConfigurationChecked: boolean) => {
    if (source === 'wellTestTable') {
      setApplyTrigger(true);
      setTimeout(() => {
        setApplyTrigger(false);
      }, 1000);

      if (wellTestWidget) {
        try {
          const visibleOrder = new Map(visibleColumns.map((key, index) => [key, index]));

          const visibleColumnsSorted = [...columnObjects]
            .filter((col) => visibleOrder.has(col.key))
            .sort((a, b) => visibleOrder.get(a.key)! - visibleOrder.get(b.key)!);

          let visibleIndex = 0;
          const reordered = columnObjects.map((col: any) => {
            if (visibleOrder.has(col.key)) {
              return visibleColumnsSorted[visibleIndex++];
            }
            return col;
          });

          const updatedColumns = reordered.map((col: any, index: number) => ({
            Order: index + 1,
            Label: col.label,
            Key: col.key,
            Selected: visibleOrder.has(col.key),
            Visible: true,
          }));

          const transformedWidget: DashboardConfig = {
            DashboardName: wellTestWidget?.widgetProperty?.dashboard ?? '',
            WidgetName: wellTestWidget?.widgetProperty?.key ?? '',
            PropertyType: 'Columns',
            Preferences: updatedColumns,
          };

          if (isConfigurationChecked) {
            const resultAction = await dispatch(saveUserPerferences(transformedWidget));

            if (typeof resultAction.payload === 'boolean') {
              if (wellTestWidget?.widgetProperty && wellTestWidget?.widgetProperty?.isDefault) {
                const clonedWellTestWidget = JSON.parse(JSON.stringify(wellTestWidget));

                clonedWellTestWidget.widgetProperty.isDefault = false;
                updateColumns(updatedColumns, clonedWellTestWidget.widgetProperty.widgetProperties.columns);
                dispatch(updateWellTestTable(clonedWellTestWidget));
              }

              setSaveConfigResponse(resultAction.payload);
              if (resultAction.payload) {
                setSuccessMessage('Successfully Saved Configuration');
                setTimeout(() => {
                  setSaveConfigResponse(false);
                }, 3000);
              } else {
                setErrorMessage('Error saving user preferences.');
              }
            } else {
              setSaveConfigResponse(false);
              setErrorMessage('Unexpected response from the server.');
            }
          }
        } catch (error) {
          console.error('Unexpected error during dispatch:', error);
          setErrorMessage('Unexpected error occurred. Please try again saving user preference.');
        }
      }
    }
  };

  useEffect(() => {
    if (wellTestIdData?.WellId && isGLDashboard) {
      dispatch(fetchInstallationsForDropdown(wellTestIdData.WellId));
    }
  }, [dispatch, wellTestIdData?.WellId, isGLDashboard, dashboardName]);

  return (
    <>
      <TableHeader
        title={
          (wellTestIdData?.Name || selectedAsset?.assetName) && (wellTestIdData?.Identifier || assetId)
            ? `Well tests - ${wellTestIdData?.Name || selectedAsset?.assetName} / ${
                wellTestIdData?.Identifier || assetId
              }`
            : 'Well tests'
        }
        onAddTestClick={() => setIsAddTestModalOpen(true)}
        disableAddTest={disableAddTest}
        wellTestTable={true}
        rPermission={rPermission}
        tableName='Well Test'
      />
      {sortedColumnObjects && sortedColumnObjects.length > 0 && (
        <TableToolbar
          ref={null}
          columns={allColumnsForToolbar
            .filter((columnId) => !fixedColumns.includes(columnId))
            .map((columnId) => ({
              id: columnId,
              Header: getColumnHeader(columnId),
              isVisible: visibleColumns.includes(columnId),
            }))}
          onColumnToggle={handleColumnToggle}
          onSaveColumns={handleSaveColumns}
          onSearch={handleSearch}
          onUpdateColumns={handleUpdateColumns}
          onApplyClicked={(_configUpdates?: any, _columns?: any, saveConfig?: boolean) =>
            handleApplyClicked('wellTestTable', saveConfig || isConfigCheckBoxClicked)
          }
          saveConfigHasbeenClicked={handleSaveConfig}
          resetToDefaultSystemClicked={() => handleReset()}
          onExportXLS={handleExportXLS}
          onInstallationSelect={handleInstallationSelect}
          hideAllInstallations={!effectivePumpChecker}
          wellTestTable={true}
          rPermission={rPermission}
          tableName='Well Test'
        />
      )}

      {sortedColumnObjects &&
        sortedColumnObjects.length > 0 &&
        (!effectivePumpChecker || (effectivePumpChecker && wellTestIdData?.WellId)) && (
          <DataTable
            ref={dataTableRef}
            key={assetId}
            visibleColumns={[...fixedColumns, ...visibleColumns]}
            searchTerm={searchTerm}
            onUpdateVisibleColumns={setVisibleColumns}
            applyTrigger={applyTrigger}
            isResetClicked={isResetClicked}
            installNumber={selectedInstallNumber}
            onFilteredDataChange={setFilteredRows}
            rPermission={rPermission}
          />
        )}
      {isAddTestModalOpen &&
        effectivePumpChecker &&
        wellTestIdData &&
        (isGLDashboard ? (
          <AddGLWellTestModal
            key={`add-gl-test-${isAddTestModalOpen}`}
            modalTitle='Add GL well test'
            buttonText='Add test'
            onClose={() => setIsAddTestModalOpen(false)}
            installNumber={selectedInstallNumber}
          />
        ) : (
          <AddWellTestModal
            key={`add-esp-test-${isAddTestModalOpen}`}
            modalTitle='Add ESP well test'
            buttonText='Add test'
            onClose={() => setIsAddTestModalOpen(false)}
            installNumber={selectedInstallNumber}
          />
        ))}

      {isAddTestModalOpen && !effectivePumpChecker && (
        <AddDefaultWellTestModal
          key={`add-t1-test-${isAddTestModalOpen}`}
          modalTitle='Add well test'
          buttonText='Add test'
          onClose={() => {
            setIsAddTestModalOpen(false);
            if (assetId) {
              dispatch(fetchDefaultWellTestDataAsync(assetId));
            }
          }}
        />
      )}

      {isAddTaskModalOpen && (
        <AddTaskModal
          isOpen={isAddTaskModalOpen}
          onClose={() => {
            setIsAddTaskModalOpen(false);
          }}
          onTaskSaved={() => {
            handleTaskSaved();
          }}
        />
      )}
      {isAddNoteWell && (
        <AddWellNoteModal
          isOpen={isAddNoteWell}
          onClose={() => {
            setIsAddNoteWell(false);
          }}
          onNoteSaved={() => {
            handleTaskSaved();
          }}
          isPC={isESP}
          currentWellId={wellId?.toString()}
          assetId={assetId}
          currentWellName={wellTestIdData?.Name}
        />
      )}
    </>
  );
}
